<template>
  <el-card>
    <h2 class="mb-6">Importación de {{ label }}</h2>

    <el-form
      :model="params"
      inline
      class="mb-6"
    >
      <slot
        :params="params"
        :onDisabledButton="handleDisabledButton"
        :updateModelValue="updateModelValue"
      ></slot>
    </el-form>
    <el-upload
      :before-upload="onFileSelected"
      :show-file-list="false"
      accept=".xls,.xlsx"
    >
      <el-button
        size="default"
        type="primary"
        :icon="IconUpload"
        :disabled="uploadButtonDisabled"
        >Seleccionar Excel</el-button
      >
    </el-upload>
    <div
      v-if="sheetNames.length"
      class="section mt-4"
    >
      <custom-select
        v-model="params.hoja"
        :label="'Hoja'"
        :spanColumn="4"
        :settings="{
          placeholder: 'Seleccionar hoja',
          options: sheetNames,
        }"
      ></custom-select>

      <small>Filas detectadas: {{ rowCount }}</small>
    </div>

    <el-form-item
      v-if="params.hoja"
      label="¿Tiene encabezados?"
    >
      <el-switch v-model="hasHeader" />
      <el-input-number
        ref="inputRef"
        v-model="params.filaEncabezado"
        :min="0"
        :disabled="!hasHeader"
        :formatter="formatter"
        :parser="parser"
        class="ml-4"
        @focus="handleFocus"
        @blur="handleBlur(params.filaEncabezado)"
      />
    </el-form-item>

    <el-button
      v-if="params.hoja"
      size="default"
      type="primary"
      @click="readColumns"
    >
      Leer columnas
    </el-button>
    <el-table
      v-if="columns.length"
      :data="columns"
    >
      <el-table-column
        prop="excelCol"
        label="Columna"
      />
      <el-table-column label="Campo">
        <template #default="{ row }">
          <el-select
            v-model="row.field"
            placeholder="—"
          >
            <el-option
              v-for="opt in fieldOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </template>
      </el-table-column>
    </el-table>

    <el-button
      v-if="columns.length"
      type="success"
      size="default"
      class="mt-4"
      @click="onProcess"
    >
      Procesar importación
    </el-button>
  </el-card>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  watch,
} from 'vue';
import * as XLSX from 'xlsx';
import {
  ElMessage,
  ElMessageBox,
} from 'element-plus';
import CustomSelect from '@/shared/components/custom_select.vue';
import api from '@/services/api';
import IconUpload from '~icons/mdi/cloud-upload';
import { useLoading } from '@/shared/composables/loading.js';
import { useLocaleNumberInput } from '@/shared/composables/useLocaleNumberInput.js';

const { inputRef, formatter, parser, handleFocus, handleBlur } = useLocaleNumberInput(null);

const props = defineProps({
  label: String, // ej: "Clientes", "Materiales"
  apiEndpoint: String, // ej: "/cliente/import"
  entityKey: String, // ej: "Cliente", "Material"
  fields: Array, // [{ label, value }…]
  requiredFields: Array, // [ 'Descripcion', 'Codigo', ... ]
  props: {
    // Lista de propiedades para el formulario
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:disabled']);

const params = reactive({
  ...Object.fromEntries(props.props.map((prop) => [prop, null])),
  hoja: '',
  filaEncabezado: 1,
});

const sheetNames = ref([]);
const workbook = ref(null);
const hasHeader = ref(true);
const fileSelected = ref(null);
const columns = ref([]);
const rowCount = ref(0);
const uploadButtonDisabled = ref(false);

const handleDisabledButton = (value) => {
  uploadButtonDisabled.value = value;
};

const updateModelValue = (value) => {
  if (Array.isArray(value.value)) {
    params[value.property] = Array.from(new Set(value.value.map((v) => v.Codigo)));
  } else {
    params[value.property] = value.value ? value.value.Codigo : null;
  }
};

const fieldOptions = computed(() => {
  const used = columns.value.map((c) => c.field);
  return [{ label: '—', value: '' }, ...props.fields.filter((f) => !used.includes(f.value))];
});

watch(
  () => params.hoja,
  (newHoja) => {
    if (newHoja) onSheetChange(newHoja);
  },
);

function onSheetChange(name) {
  const ws = workbook.value.Sheets[name];
  const range = XLSX.utils.decode_range(ws['!ref']);
  rowCount.value = range.e.r - range.s.r + 1;
}

watch(hasHeader, (v) => {
  params.filaEncabezado = v ? 1 : 0;
});

function onFileSelected(file) {
  const realFile = file.raw instanceof File ? file.raw : file;
  fileSelected.value = realFile;
  const reader = new FileReader();
  reader.onload = (e) => {
    const data = new Uint8Array(e.target.result);
    workbook.value = XLSX.read(data, { type: 'array' });
    sheetNames.value = workbook.value.SheetNames;
  };
  reader.readAsArrayBuffer(realFile);
  return false;
}

function readColumns() {
  const ws = workbook.value.Sheets[params.hoja];
  const range = XLSX.utils.decode_range(ws['!ref']);
  const headers = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    if (!hasHeader.value) {
      headers.push(XLSX.utils.encode_col(C));
    } else {
      const addr = XLSX.utils.encode_cell({ c: C, r: params.filaEncabezado - 1 });
      headers.push(ws[addr]?.v?.toString() || XLSX.utils.encode_col(C));
    }
  }
  columns.value = headers.map((col, idx) => ({
    excelCol: col,
    colIndex: idx,
    field: '',
  }));
}

function resetImport() {
  for (const prop of props.props) {
    params[prop] = null;
  }
  params.hoja = '';
  params.filaEncabezado = 1;
  sheetNames.value = [];
  workbook.value = null;
  hasHeader.value = true;
  fileSelected.value = null;
  columns.value = [];
  rowCount.value = 0;

  handleDisabledButton(true);
}

async function onProcess() {
  for (const f of props.requiredFields) {
    if (!columns.value.some((c) => c.field === f)) {
      return ElMessage.error(`Debes mapear el campo obligatorio: ${f}`);
    }
  }
  if (!fileSelected.value) return ElMessage.error('Seleccionar archivo');
  if (!params.hoja) return ElMessage.error('Seleccionar hoja');

  const ws = workbook.value.Sheets[params.hoja];

  const rawData = XLSX.utils.sheet_to_json(ws, {
    header: columns.value.map((m) => m.field),
    range: params.filaEncabezado,
    defval: null,
  });

  const filteredData = rawData.filter((row) => {
    return props.requiredFields.every((f) => row[f]);
  });

  const { filaEncabezado, hoja, ...catalogData } = params;

  const payload = filteredData.map((row) => ({
    ...catalogData,
    ...row,
  }));

  try {
    useLoading().openLoading();
    await api.post(props.apiEndpoint, JSON.stringify(payload), {
      headers: { 'Content-Type': 'application/json' },
    });
    ElMessage.success('Importación exitosa');
    resetImport();
  } catch {
    ElMessage.error('Error en importación');
  } finally {
    useLoading().closeLoading();
  }
}
</script>
<style scoped>
.section {
  margin-bottom: 2rem;
}
.custom-label {
  font-weight: 700;
  color: #111;
}
.mb-6 {
  margin-bottom: 1.5rem;
}
.mt-4 {
  margin-top: 1rem;
}
.mt-8 {
  margin-top: 2rem;
}
.ml-4 {
  margin-left: 1rem;
}
</style>
